/**
 * DOM selection utilities using jQuery-like CSS selector syntax.
 * Returns a wrapper with chainable methods: .val(), .text(), .parent(), .children()
 * Supports: #id, .class, tag, [attr], tag.class, tag#id, combinators, etc.
 */

const defaultContext = document

const VALUE_ELEMENTS = ['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON']

function hasValueProperty(element) {
    return element && VALUE_ELEMENTS.includes(element.tagName)
}

/**
 * Creates a wrapper around a DOM element with chainable methods.
 * @param {Element|null} element - The DOM element to wrap
 * @returns {Object} Wrapper with .val(), .text(), .parent(), .children(), .get()
 */
function createWrapper(element) {
    return {
        /**
         * Returns the value attribute (for input, select, textarea, button).
         * Returns null if the element has no value property.
         */
        val() {
            return hasValueProperty(element) ? element.value : null
        },

        /**
         * Returns the innerHTML of the element.
         * Returns null if element is null.
         */
        text() {
            return element ? element.innerHTML : null
        },

        /**
         * Returns a wrapper around the parent element.
         */
        parent() {
            return createWrapper(element?.parentElement ?? null)
        },

        /**
         * Returns an array of wrappers for each direct child element.
         */
        children() {
            if (!element?.children) return []
            return Array.from(element.children).map((child) =>
                createWrapper(child)
            )
        },

        /**
         * Returns the raw DOM element.
         */
        el() {
            return element ?? null
        },
    }
}

/**
 * Selects the first element and returns a wrapper with chainable methods.
 * @param {string} selector - jQuery-style CSS selector (#id, .class, tag, etc.)
 * @param {Document|Element} [context=document] - Root element to search within
 * @returns {Object} Wrapper with .val(), .text(), .parent(), .children(), .get()
 * @example
 * $('#user').val()        // value of input/select/textarea, or null
 * $('#contenedor').text() // innerHTML
 * $('#user').parent()    // wrapper of parent element
 * $('#contenedor').children() // array of child wrappers
 */
export function $(selector, context = defaultContext) {
    if (!selector || typeof selector !== 'string') {
        return createWrapper(null)
    }
    const element = context.querySelector(selector)
    return createWrapper(element)
}

/**
 * Returns the raw first element matching the selector (no wrapper).
 */
export function select(selector, context = defaultContext) {
    if (!selector || typeof selector !== 'string') return null
    return context.querySelector(selector)
}

/**
 * Returns all raw elements matching the selector as an array (no wrappers).
 */
export function selectAll(selector, context = defaultContext) {
    if (!selector || typeof selector !== 'string') return []
    return Array.from(context.querySelectorAll(selector))
}

/**
 * Selects all matching elements and returns an array of wrappers.
 * @param {string} selector - jQuery-style CSS selector
 * @param {Document|Element} [context=document] - Root element to search within
 * @returns {Object[]} Array of wrappers
 */
export function $$(selector, context = defaultContext) {
    if (!selector || typeof selector !== 'string') return []
    const elements = context.querySelectorAll(selector)
    return Array.from(elements).map((el) => createWrapper(el))
}
